import { push } from "connected-react-router"
import { Loader } from "dash-styled"
import { FC, useCallback, useEffect, useMemo } from "react"
import { useDispatch } from "react-redux"
import type { PrelabelResponse } from "resha"
import { SchemaItem } from "universal-schema/lib"
import {
    startTaskDetailsPoll,
    startTaskListPoll,
    stopTaskDetailsPoll,
    stopTaskListPoll
} from "../../../../../../../Actions/API/Job/TaskActions"
import { useAppDetailsPaths } from "../../../../../../../Hooks/useAppDetailsPaths"
import { useBoolean } from "../../../../../../../Hooks/useBoolean"
import { useDereference } from "../../../../../../../Hooks/useDereference"
import { useTaskDetails } from "../../../../../../../Hooks/useTaskDetails"
import { useUrlSigner } from "../../../../../../../Hooks/useUrlSigner"
import { APIAppInstanceModel } from "../../../../../../../Models/API/AppInstanceModel"
import {
    APIJobModel,
    APIJobQueryModel,
    JobType,
    JobVersionItem
} from "../../../../../../../Models/API/JobModel"
import {
    APITaskDetailsModel,
    TaskListQueryParams,
    UITaskModel
} from "../../../../../../../Models/API/TaskModel"
import { DataAccess } from "../../../../../../../Models/Client/DataAccess"
import {
    getValue,
    hasValue
} from "../../../../../../../Models/Client/FetchState"
import { FilterAndSortExpression } from "../../../../../../../Models/Client/FilterAndSortExpression"
import { MainThunkDispatch } from "../../../../../../../Reducers"
import { DetailViewHeader } from "../Header/DetailViewHeader"
import { TaskDetailInputOutput } from "./InputOutput_deprecated/TaskDetailInputOutput"
import { TaskDetailViewSubheader } from "./Subheader/TaskDetailViewSubheader"
import { TaskDetails } from "./TaskDetails_deprecated/TaskDetails"

/**
 * @deprecated
 */
export interface TaskDetailViewProps {
    className?: string
    orgUsername: string
    appID: string
    job: APIJobModel
    taskID: string
    instance: APIAppInstanceModel
    versionsList: JobVersionItem[]
    selectedVersion: number
    filterAndSort: FilterAndSortExpression<APIJobQueryModel>
    prelabels: PrelabelResponse
    showOldVersion: boolean
    taskList: UITaskModel[]
    onAddTags?: (value: string, isNew: boolean) => Promise<void>
    onRemoveTags?: (value: string) => Promise<void>
    onChangeSelectedVersion: (version: number) => void
    onTaskChange: (taskID: string) => void
}

/**
 * @deprecated
 */
export const TaskDetailView: FC<TaskDetailViewProps> = ({
    className,
    orgUsername,
    appID,
    job,
    taskID,
    instance,
    filterAndSort,
    selectedVersion,
    versionsList,
    prelabels,
    showOldVersion,
    taskList,
    onAddTags,
    onRemoveTags,
    onChangeSelectedVersion,
    onTaskChange
}) => {
    const dispatch = useDispatch<MainThunkDispatch>()
    const [isJsonShown, showJson, showFormatted] = useBoolean(false)
    const [, , jobsDetailPath] = useAppDetailsPaths()
    const dataAccess = useMemo(
        () =>
            job.jobType === JobType.COLLABORATOR
                ? DataAccess.perCollabTask(taskID)
                : DataAccess.perTask(taskID),
        [job.jobType, taskID]
    )
    const urlSigner = useUrlSigner(dataAccess)
    const currentJob = versionsList[selectedVersion]

    const selectedTask = useMemo(
        () => taskList.find(task => task.id.toString() === taskID),
        [taskID, taskList]
    )

    const taskDetailsData = useTaskDetails(currentJob.versionJobId, taskID)

    const taskResponseFetch = useDereference(
        job.id === undefined ? undefined : getValue(taskDetailsData)?.response,
        dataAccess
    )
    const taskDetails = useMemo<APITaskDetailsModel | undefined>(() => {
        if (!hasValue(taskDetailsData)) {
            return undefined
        }

        return {
            ...taskDetailsData.value,
            response: getValue(taskResponseFetch) as SchemaItem[] | undefined
        }
    }, [taskDetailsData, taskResponseFetch])

    const goBack = useCallback(() => {
        dispatch(
            push(jobsDetailPath(orgUsername, appID, job.id, filterAndSort))
        )
    }, [appID, dispatch, filterAndSort, job.id, jobsDetailPath, orgUsername])

    const handleChangeSelectedVersion = useCallback(
        (version: number) => {
            onChangeSelectedVersion(version)
            goBack()
        },
        [goBack, onChangeSelectedVersion]
    )

    useEffect(
        () => {
            dispatch(
                startTaskListPoll(currentJob.versionJobId, TaskListQueryParams)
            )
            dispatch(startTaskDetailsPoll(currentJob.versionJobId, taskID))

            return () => {
                dispatch(stopTaskListPoll())
                dispatch(stopTaskDetailsPoll())
            }
        }, // taskDetails?.correct helps in refreshing the list when task correctness change
        [currentJob.versionJobId, dispatch, taskID, taskDetails?.correct]
    )

    return (
        <div className={`relative flex flex-col ${className ?? ""}`}>
            <DetailViewHeader
                orgUsername={orgUsername}
                job={job}
                instance={instance}
                sort={filterAndSort.sort}
                versionsList={versionsList}
                selectedVersion={selectedVersion}
                showOldVersion={showOldVersion}
                onAddTag={onAddTags}
                onRemoveTag={onRemoveTags}
                onChangeSelectedVersion={handleChangeSelectedVersion}
            />
            <TaskDetailViewSubheader
                className="mt-4 mb-4"
                appID={appID}
                selectedTask={selectedTask}
                totalTasks={taskList.length}
                isJsonShown={isJsonShown}
                showFormatted={showFormatted}
                showJson={showJson}
                goBack={goBack}
                onTaskChange={(taskNumber: number) => {
                    const task = taskList.find(
                        task => task.taskNumber === taskNumber
                    )
                    // if task not found, do nothing
                    if (task !== undefined) {
                        onTaskChange(task.id)
                    }
                }}
            />
            {taskDetails === undefined ? (
                <Loader absolute={true} size="xxxl" mask={false} />
            ) : (
                <>
                    <div className="flex flex-col flex-grow overflow-hidden overflow-y-scroll">
                        <TaskDetailInputOutput
                            instance={instance}
                            taskDetails={taskDetails}
                            isJsonShown={isJsonShown}
                            prelabels={prelabels}
                            urlSigner={urlSigner}
                        />
                        {taskDetails !== undefined && (
                            <TaskDetails
                                appID={appID}
                                taskDetails={taskDetails}
                                taskNumber={selectedTask?.taskNumber ?? 0}
                            />
                        )}
                    </div>
                </>
            )}
        </div>
    )
}
